import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { getDefaultAccountType } from "../../externalApi/AuthenticatorApi";
import { lazyLoadModule } from "../../lazyLoadModule";
import { WidgetApi } from "../orchestrator/WidgetApi";
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../telemetry/Telemetry");
// This authenticator object is used by a widget to request properties and tokens on the account that has been selected for it.
// V1 this will be the single default account.  V2 the inner account object may be changed by the user.
export class WidgetAuthenticator extends WidgetApi {
    constructor(widgetInstance) {
        super(widgetInstance);
    }
    // Props available are values such as:
    //  AccountId, UPN, DisplayName, OID, Authority, FirstName, LastName,  MUID, ProviderAuthority (i.e. "consumers"|"organizations")
    async getProperty(name) {
        const authenticator = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("authenticator", 1, 1);
        if (authenticator) {
            let accountId = await this.getAccountId();
            return await authenticator.getAccountPropertyAsync(accountId, name);
        }
        return undefined;
    }
    async getToken(clientId, scope, resource, apiVersion) {
        // Create a local callback that completes the promise.
        let completed = new Promise(async (resolve, reject) => {
            function tokenReceived(thisInstance, token, errorResult, userInteractionRequired) {
                if (!token || token === "") {
                    lazyLoadModule(TelemetryModule).then((Telemetry) => {
                        Telemetry.logEvent(dashboard, "WebAppWidgetAuthenticatorGetTokenFailed", 16777216 /* productAndServicePerformance */, {
                            apiVersion: apiVersion,
                            appId: thisInstance._widgetInstance.widgetRegistration.appId,
                            clientId: clientId,
                            errorResult: errorResult,
                            providerId: thisInstance._widgetInstance.widgetRegistration.providerId,
                            resource: resource,
                            scope: scope,
                            userInteractionRequired: userInteractionRequired,
                            widgetDefinitionId: thisInstance._widgetInstance.widgetDefinitionId,
                            widgetInstanceId: thisInstance._widgetInstance.widgetId,
                        });
                    });
                    // Only if the error was a result of UserInteractionRequired state*, put the widget
                    // into error state, otherwise let the widget try to render what it can.
                    // *If the value us undefined, the host app did not send this value, use previous behavior
                    if (userInteractionRequired === undefined || userInteractionRequired) {
                        // In phase1, if the Non-Signed-In user is enabled and the widget is the feed widget,
                        // the host app should show the page instead of setting the state to error to show the  error page
                        const isFeedWidget = thisInstance._widgetInstance.widgetId.toLowerCase() === "com.msn.desktopfeed";
                        if (isFeedWidget) {
                            return;
                        }
                        thisInstance._widgetInstance.widgetErrorStateReason = "GetTokenFailure";
                        thisInstance._widgetInstance.widgetErrorCode = errorResult;
                        thisInstance._widgetInstance.widgetState = "error";
                    }
                }
            }
            function accountRequestFailed(thisInstance) {
                Trace.logEvent(dashboard, "WebAppWidgetAuthenticatorGetAccountFailed", 3 /* error */, {
                    apiVersion: apiVersion,
                    appId: thisInstance._widgetInstance.widgetRegistration.appId,
                    clientId: clientId,
                    providerId: thisInstance._widgetInstance.widgetRegistration.providerId,
                    resource: resource,
                    scope: scope,
                    widgetDefinitionId: thisInstance._widgetInstance.widgetDefinitionId,
                    widgetInstanceId: thisInstance._widgetInstance.widgetId,
                });
            }
            const authenticator = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("authenticator", 1, 1);
            if (authenticator) {
                let accountId = await this.getAccountId();
                if (accountId !== "") {
                    // Automation parameters are added in reverse in the host. 'token' is sent as param[2], userInteractionRequiredas param[0]
                    let result = await authenticator.getAccountTokenAsync(accountId, clientId, scope, resource, apiVersion, this._widgetInstance.widgetId);
                    tokenReceived(this, result.token, result.errorResult, result.interactionRequired);
                    resolve(result.token);
                }
                else {
                    accountRequestFailed(this);
                    reject("WebAppWidgetAuthenticatorGetAccountFailed");
                }
            }
            reject("WebAppWidgetAuthenticatorGetAccountFailed");
        });
        return completed;
    }
    async getAccountId() {
        // AccountId has not been assigned
        if (this._widgetInstance.accountId === "") {
            const authenticator = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("authenticator", 1, 1);
            if (authenticator) {
                this._widgetInstance.accountId = await authenticator.getDefaultAccountIdAsync();
                // Finds SupportedAuth in widget registration
                const supportedAuth = this._widgetInstance.widgetRegistration.supportedAuth;
                if (supportedAuth) {
                    let accountState = "DefaultAccountFound";
                    const defaultAccountType = await getDefaultAccountType();
                    const isDefaultAccountValid = supportedAuth.find((auth) => auth.toLowerCase() === defaultAccountType.toLowerCase()) !== undefined;
                    const isMSASupported = supportedAuth.find((auth) => auth.toLowerCase() === "msa") !== undefined;
                    const isAADSupported = supportedAuth.find((auth) => auth.toLowerCase() === "aad") !== undefined;
                    const isNONESupported = supportedAuth.find((auth) => auth.toLowerCase() === "none") !== undefined;
                    // Looking to load first compatible account for non-peregrine widget
                    if (!isDefaultAccountValid && this._widgetInstance.widgetRegistration.entryPoint.className !== "peregrine-widgets") {
                        const validAccountId = (await authenticator.getAccountIdsAsync()).find(async (accountId) => {
                            (isMSASupported && (await authenticator.isMSAAccountAsync(accountId))) ||
                                (isAADSupported && (await authenticator.isAADAccountAsync(accountId)));
                        });
                        if (validAccountId != undefined) {
                            this._widgetInstance.accountId = validAccountId;
                            accountState = "SecondaryAccountFound";
                        }
                        else {
                            this._widgetInstance.accountId = await authenticator.getDefaultAccountIdAsync();
                            accountState = "NoSecondaryAccountFound";
                        }
                    }
                    Trace.logEvent(dashboard, "WidgetAuthenticatorGetAccountId", 0 /* verbose */, {
                        accountIdState: accountState,
                        appId: this._widgetInstance.widgetRegistration.appId,
                        defaultAccountType: defaultAccountType,
                        isAADSupported: isAADSupported,
                        isMSASupported: isMSASupported,
                        isNONESupported: isNONESupported,
                        providerId: this._widgetInstance.widgetRegistration.providerId,
                        widgetDefinitionId: this._widgetInstance.widgetDefinitionId,
                        widgetInstanceId: this._widgetInstance.widgetId,
                    });
                }
            }
        }
        return this._widgetInstance.accountId;
    }
}
